// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { type PageAmendment } from './types'

interface AmendmentsByPageDisplayProps {
  amendments: PageAmendment[]
}

// Helper function to get combined formatting classes and inline styles
const getFormattingClassesAndStyle = (types: ('strikeout' | 'underline' | 'italics')[]): { className: string; style: React.CSSProperties } => {
  const classes = []
  const textDecorations = []
  
  // Apply italic class
  if (types.includes('italics')) {
    classes.push('italic')
  }
  
  // Build text-decoration values
  if (types.includes('strikeout')) {
    textDecorations.push('line-through')
  }
  if (types.includes('underline')) {
    textDecorations.push('underline')
  }
  
  const style: React.CSSProperties = {}
  if (textDecorations.length > 0) {
    style.textDecoration = textDecorations.join(' ')
  }
  
  return {
    className: classes.join(' '),
    style
  }
}

export function AmendmentsByPageDisplay({ amendments }: AmendmentsByPageDisplayProps) {
  // Group amendments by page
  const amendmentsByPage = amendments.reduce((acc, amendment) => {
    if (!acc[amendment.page_number]) {
      acc[amendment.page_number] = []
    }
    acc[amendment.page_number].push(amendment)
    return acc
  }, {} as Record<number, PageAmendment[]>)

  return (
    <div className="space-y-4">
      {Object.entries(amendmentsByPage)
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([pageNumber, pageAmendments]) => {
          // Group amendments by line number
          const amendmentsByLine = pageAmendments.reduce((acc, amendment) => {
            const lineKey = amendment.line_number?.toString() || 'no-line'
            if (!acc[lineKey]) {
              acc[lineKey] = []
            }
            acc[lineKey].push(amendment)
            return acc
          }, {} as Record<string, PageAmendment[]>)

          return (
            <Card key={pageNumber}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Page {pageNumber}</span>
                  <Badge variant="outline">
                    {pageAmendments.length} {pageAmendments.length === 1 ? 'amendment' : 'amendments'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(amendmentsByLine)
                    .sort(([a], [b]) => {
                      if (a === 'no-line') return 1
                      if (b === 'no-line') return -1
                      return parseInt(a) - parseInt(b)
                    })
                    .map(([lineKey, lineAmendments]) => (
                      <div key={lineKey} className="border-l-4 border-l-slate-200 dark:border-l-slate-700 pl-4 py-2">
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 mt-0.5">
                            {lineKey !== 'no-line' && (
                              <Badge variant="outline" className="text-xs mb-2">
                                Line {lineKey}
                              </Badge>
                            )}
                            <div className="flex flex-wrap gap-1">
                              {/* Show all unique amendment types for this line */}
                              {Array.from(new Set(lineAmendments.flatMap(a => a.types))).map((type) => (
                                <Badge 
                                  key={type}
                                  variant={
                                    type === 'strikeout' ? 'destructive' : 
                                    type === 'underline' ? 'default' : 
                                    'secondary'
                                  }
                                  className="text-xs"
                                >
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="font-mono text-sm bg-white dark:bg-slate-900 border rounded p-2 space-y-1">
                              {lineAmendments.map((amendment, idx) => {
                                const { className, style } = getFormattingClassesAndStyle(amendment.types)
                                return (
                                  <div key={idx}>
                                    <span className={className} style={style}>
                                      {amendment.text}
                                    </span>
                                    {idx < lineAmendments.length - 1 && <span className="text-muted-foreground"> </span>}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )
        })}
    </div>
  )
}
